import React, { useLayoutEffect, useState } from 'react';
import { ResponsiveBar } from '@nivo/bar'
import { findIndex, flatten, isObject, map, maxBy, sortBy } from 'lodash';

import { ColorPalette } from 'types/constants';
import { opacity } from 'utilities/color';

import COLORS from '../colors';
import ChartContainer from '../container';
import Legend from '../legend'

import colors from '@magoosh/lib/styles/colors.module.scss';
import style from '../style.module.scss';

const fontFamily = '"Tondo", Arial, "Helvetica Neue", Helvetica, sans-serif';

interface Props {
  chartProps?: any;
  data: { [key: string]: number | string; }[];
  formatValueFn?: (value: number) => string,
  hideLegend?: boolean;
  horizontal?: boolean
  indexBy: string;
  legendPosition?: 'bottom' | 'left' | 'right' | 'top';
}

const BarChart: React.FC<Props> = (props) => {
  const formattedData = (sourceData) => {
    let colorsIdx = 0;
    let keyToColorMapping = {};

    return sourceData.map((datum) => {
      let resultDatum = {};

      Object.keys(datum).forEach((key) => {
        if (key === props.indexBy) {
          // Don't need to assign colors to the key we are indexing by
          resultDatum[key] = datum[key];
        } else if (key.match(/.*Color$/)) {
          // There was a color manually specified in the data. Save it so we can apply it later
          //to any data for the same key that is missing a color definition.
          resultDatum[key] = datum[key];
          keyToColorMapping[key.slice(0, key.length - 5)] = datum[key];
        } else {
          resultDatum[key] = datum[key];

          // If the key doesn't have a corresponding color, assign it from either a
          // previous color we've seen in the data or the defaults.
          if (!datum[`${key}Color`]) {
            const color = keyToColorMapping[key] || COLORS[colorsIdx % COLORS.length];
            resultDatum[`${key}Color`] = color
            keyToColorMapping[key] = color;
            colorsIdx += 1;
          }
        }
      });

      return resultDatum;
    });
  };

  const legendGroups = (keys, sourceData) => {
    return keys.map((key) => (
      {
        // Every datum has the same color data so we can just look at the first one
        colors: [sourceData[0][`${key}Color`]],
        name: key
      }
    ));
  };

  const uniqueColors = (sourceData) => {
    const colorsObserved = new Set();

    sourceData.forEach((datum) => {
      Object.keys(datum).forEach((key) => {
        if (key.match(/.*Color$/)) {
          colorsObserved.add(datum[key]);
        }
      });
    });

    return Array.from(colorsObserved) as string[];
  };

  const uniqueKeys = (sourceData) => {
    const keysObserved = new Set();

    // Nivo wants to display the keys in the reverse order we specify them
    sourceData.reverse().forEach((datum) => {
      Object.keys(datum).forEach((key) => {
        if (key === props.indexBy) return;
        if (key.match(/.*Color$/)) return;

        keysObserved.add(key);
      });
    });

    return Array.from(keysObserved) as string[];
  };

  const uniqueSeries = (sourceData) => {
    const keysObserved = new Set();

    sourceData.forEach((datum) => {
      Object.keys(datum).forEach((key) => {
        if (key === props.indexBy) {
          keysObserved.add(datum[key]);
        }
      });
    });

    return Array.from(keysObserved) as string[];
  };

  const data = formattedData(props.data);
  const colorsInData = uniqueColors(data);
  const keysInData = uniqueKeys(data);
  const seriesInData = uniqueSeries(data);

  const [seriesLabelWidth, setSeriesLabelWidth] = useState(0);

  useLayoutEffect(() => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = `14px ${fontFamily}`;

    const width = context.measureText(
      maxBy(seriesInData, (seriesName) => seriesName.length)
    ).width;

    setSeriesLabelWidth(width);
  }, [props.data]);

  return (
    <ChartContainer legendPosition={props.legendPosition}>
      <div
        className={style.chart}
        style={{ height: data.length * 25, position: 'relative', width: '100%' }}
      >
        <ResponsiveBar
          axisLeft={{
            tickPadding: 10,
            tickSize: 0
          }}
          colors={colorsInData}
          data={data}
          enableLabel={false}
          indexBy={props.indexBy}
          innerPadding={2}
          layout={props.horizontal ? 'horizontal' : 'vertical'}
          keys={keysInData}
          margin={{ left: seriesLabelWidth }}
          padding={0.3}
          theme={{
            text: {
              fontFamily,
              fontSize: 14
            }
          }}
          valueFormat={props.formatValueFn}
          {...props.chartProps}
        />
      </div>

      {!props.hideLegend && <Legend groups={legendGroups(keysInData, data)} />}
    </ChartContainer>
  );
};

interface GroupProps {
  chartProps?: any;
  data: { [key: string]: number | string; }[][];
  formatValueFn?: (value: number) => string,
  hideLegend?: boolean;
  horizontal?: boolean
  indexBy: string;
  legendPosition?: 'bottom' | 'left' | 'right' | 'top';
}

const BarChartGroup: React.FC<GroupProps> = (props) => {
  const [seriesLabelWidth, setSeriesLabelWidth] = useState(0);

  const uniqueSeries = () => {
    const keysObserved = new Set();

    props.data.forEach((group) => {
      group.forEach((datum) => {
        Object.keys(datum).forEach((key) => {
          if (key === 'type') {
            keysObserved.add(datum[key]);
          }
        });
      });
    });

    return Array.from(keysObserved) as string[];
  };

  // Because we have multiple charts grouped together we need to calculate the maximum label
  // width and pass it into to each of the charts, rather than letting each chart calculate itself
  useLayoutEffect(() => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = `14px ${fontFamily}`;

    const width = context.measureText(
      maxBy(uniqueSeries(), (seriesName) => seriesName.length)
    ).width;

    setSeriesLabelWidth(width);
  }, [props.data]);

  const legendGroups = () => {
    const legendGroupings = [];

    props.data.forEach((group) => {
      group.forEach((dataPoints) => {
        Object.keys(dataPoints).forEach((key) => {
          if (key === 'type') return;
          if (key.match(/.*Color$/)) return;

          const groupIndex = findIndex(legendGroupings, (g) => g.name === key);

          if (groupIndex === -1) {
            legendGroupings.push({ colors: [dataPoints[`${key}Color`]], name: key });
          }
        })
      });
    });

    return legendGroupings;
  };

  return (
    <ChartContainer legendPosition={props.legendPosition}>
      <div className="d-flex flex-column gap-xs" style={{ width: '100%' }}>
        {
          props.data.map((group, idx) => (
            <React.Fragment key={idx}>
              {idx !== 0 && <hr className="u-margin-A-n" />}

              <BarChart
                chartProps={{ animate: false, margin: { left: seriesLabelWidth } }}
                data={group}
                formatValueFn={props.formatValueFn}
                hideLegend
                horizontal
                indexBy={props.indexBy}
              />
            </React.Fragment>
          ))
        }
      </div>

      <Legend groups={legendGroups()} />
    </ChartContainer>
  );
}

export { BarChart, BarChartGroup };
